// import { useEffect, useState } from "react";
import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../App";
import { HttpRetCode } from "../webResponse/enums";
import { getFromLocalStorage } from "utils/localStorage/localStorage.utils";
import { AxiosError } from "axios";
import { ApiResult } from "types/apiresponses";

export const isAuthenticated = async () => {
  try {
    const response = await axiosInstance.get("/check-auth");
    return { isAuth: true, Status: response.status, Data: response.data };
  } catch (e) {
    const response = (e as AxiosError).response?.data as ApiResult<any>;
    return { isAuth: false, Status: HttpRetCode.UNAUTHORIZED, Data: response };
  }
};

export const refreshToken = async () => {
  try {
    const response = await axiosInstance.post("/auth/refresh");
    return response.status === HttpRetCode.OK;
  } catch (error) {
    console.error("Unable to refresh token", error);
  }
};

export const useAuth = (): {
  isAuth: boolean;
  loading: boolean;
  isLoggedOn: boolean;
  Data?: any;
  Status?: number;
} => {
  try {
    const [isAuth, setIsAuth] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isLoggedOn, setIsLoggedOn] = useState(false);
    const [data, setData] = useState<any>(null);
    const [status, setStatus] = useState<number>(HttpRetCode.UNAUTHORIZED);

    const checkAuth = useCallback(async () => {
      try {
        // First, check if user exists in localStorage (restored session)
        const User = getFromLocalStorage<any>("user");

        if (User === null) {
          setIsAuth(false);
          setIsLoggedOn(false);
          setLoading(false);
          setStatus(HttpRetCode.UNAUTHORIZED);
          return;
        }

        if (User) {
          setIsAuth(true);
          setLoading(false);
          setIsLoggedOn(true);
          setData(User);
        }

        // Then verify with server in background
        const response = await isAuthenticated();
        if (response.isAuth) {
          setData(response.Data);
          setIsLoggedOn(true);
          setIsAuth(true);
        } else {
          setIsAuth(false);
          setIsLoggedOn(
            response.Data?.code === "EXPIRED_SESION" ? false : isLoggedOn,
          );
        }
        setStatus(response.Status);
      } catch (error) {
        console.error("Auth check failed:", error);
        // Only logout if no localStorage user AND server check failed
        const userJson = getFromLocalStorage("user");
        if (!userJson) {
          setIsAuth(false);
          setIsLoggedOn(false);
        }
        setLoading(false);
        setStatus(HttpRetCode.UNAUTHORIZED);
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      checkAuth();
      // Set up interval to check auth every 30 seconds
      const interval = setInterval(checkAuth, 1 * 30000);
      return () => clearInterval(interval);
    }, [checkAuth]);

    return { isAuth, loading, isLoggedOn, Data: data, Status: status };
  } catch (error) {
    console.error("useAuth error:", error);
    return {
      isAuth: false,
      loading: true,
      isLoggedOn: false,
      Data: null,
      Status: HttpRetCode.UNAUTHORIZED,
    };
  }
};

export default useAuth;
